/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Tiptap Slash Commands Suggestion Extension (Task 10.2.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { Extension, type Editor, type Range } from '@tiptap/core';
import Suggestion, {
  type SuggestionOptions,
  type SuggestionProps,
  type SuggestionKeyDownProps,
} from '@tiptap/suggestion';
import { ReactRenderer } from '@tiptap/react';
import {
  SlashCommandMenu,
  type SlashCommandMenuRef,
} from './SlashCommandMenu';
import {
  filterSlashCommands,
  type SlashCommandItem,
} from './slash-commands';

export interface SlashCommandsOptions {
  suggestion: Omit<SuggestionOptions<SlashCommandItem>, 'editor'>;
}

export const SlashCommandsExtension = Extension.create<SlashCommandsOptions>({
  name: 'slashCommands',

  addOptions() {
    return {
      suggestion: {
        char: '/',
        command: ({ editor, range, props }: { editor: Editor; range: Range; props: SlashCommandItem }) => {
          props.command({ editor, range });
        },
        items: ({ query }: { query: string }) => {
          return filterSlashCommands(query);
        },
        render: () => {
          let component: ReactRenderer<SlashCommandMenuRef> | null = null;
          let popup: HTMLDivElement | null = null;

          return {
            onStart: (props: SuggestionProps<SlashCommandItem>) => {
              component = new ReactRenderer(SlashCommandMenu, {
                props,
                editor: props.editor,
              });

              if (!props.clientRect) {
                return;
              }

              popup = document.createElement('div');
              popup.style.position = 'fixed';
              popup.style.zIndex = '99999';
              popup.style.pointerEvents = 'auto';
              popup.appendChild(component.element);
              document.body.appendChild(popup);

              const rect = props.clientRect();
              if (rect && popup) {
                const top = rect.bottom + 6;
                const left = Math.min(rect.left, window.innerWidth - 360);
                popup.style.top = `${top}px`;
                popup.style.left = `${Math.max(16, left)}px`;
              }
            },

            onUpdate: (props: SuggestionProps<SlashCommandItem>) => {
              component?.updateProps(props);

              if (!props.clientRect || !popup) {
                return;
              }

              const rect = props.clientRect();
              if (rect) {
                const top = rect.bottom + 6;
                const left = Math.min(rect.left, window.innerWidth - 360);
                popup.style.top = `${top}px`;
                popup.style.left = `${Math.max(16, left)}px`;
              }
            },

            onKeyDown: (props: SuggestionKeyDownProps) => {
              if (props.event.key === 'Escape') {
                if (popup && popup.parentNode) {
                  popup.parentNode.removeChild(popup);
                }
                component?.destroy();
                popup = null;
                component = null;
                return true;
              }

              return component?.ref?.onKeyDown(props) ?? false;
            },

            onExit: () => {
              if (popup && popup.parentNode) {
                popup.parentNode.removeChild(popup);
              }
              component?.destroy();
              popup = null;
              component = null;
            },
          };
        },
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});
